import { useLingui } from "@lingui/react";
import { type ReactNode, use } from "react";

import { cn } from "@/shared/lib";
import { SeverityDot } from "@/shared/ui/severity-dot";

import type { Finding, FindingCode, FindingSeverity } from "../model/Finding";
import { FIX_TIPS, METRIC_TIPS, ROW_TIPS } from "../model/FIX_TIPS";
import { SEVERITY_LABELS } from "../model/SEVERITY_LABELS";
import { FindingContext } from "./FindingContext";
import { FindingFixes } from "./FindingFixes";
import type { FindingVariant } from "./FindingProps";
import { FixTip } from "./FixTip";

const COMPACT_COLORS: Record<FindingSeverity, string> = {
  good: cn("border-good/40 bg-good/10"),
  warn: cn("border-warn/40 bg-warn/10"),
  bad: cn("border-bad/40 bg-bad/10"),
};

type FindingCardProps = {
  code: FindingCode;
  severity: FindingSeverity;
  variant?: FindingVariant | undefined;
  icon: ReactNode;
  title: string;
  details: string;
  // Пояснение в одну-две строки для `compact`.
  summary: string;
  // Значок с подсказкой у заголовка (`FindingHint`): подробнее, в чём проблема и как исправить.
  hint?: ReactNode;
  children?: ReactNode;
};

// Совет «как исправить» для строки: общий по коду (у метрики — по метрике), а у находок, где
// совет живёт в пояснении карточки, — строчный. У сильных сторон нет.
const rowTipOf = (finding: Finding | null) =>
  finding === null || finding.severity === "good"
    ? undefined
    : finding.code === "metric"
      ? METRIC_TIPS[finding.params.metric]
      : (FIX_TIPS[finding.code] ?? ROW_TIPS[finding.code]);

// Общая раскладка находки: иконка, заголовок, пояснение, оценка. `compact` и `row` — без оценки
// и вложенных подробностей, с коротким пояснением: `compact` — плашка цвета оценки,
// `row` — строка без плашки (цвет задаёт блок вокруг) и общий совет под ней. Полный вид
// и `lead` (крупно, «Главное в этом матче», рамку даёт блок вокруг) внизу показывают советы
// «как исправить» находки из `FindingContext` (его ставит `FindingView`).
export const FindingCard = ({
  code,
  severity,
  variant = "full",
  icon,
  title,
  details,
  summary,
  hint,
  children,
}: FindingCardProps) => {
  const { _ } = useLingui();
  const finding = use(FindingContext);
  if (variant === "row") {
    const tip = rowTipOf(finding);
    return (
      <article data-finding={code} data-severity={severity} className="flex items-start gap-3">
        {icon}
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <h3 className="text-[15px] font-semibold text-foreground">
              <span className="sr-only">{_(SEVERITY_LABELS[severity])}: </span>
              {title}
            </h3>
            {hint}
          </div>
          <p className="text-[13px] text-muted">{summary}</p>
          {tip !== undefined && (
            <div className="text-[13px] [&>p]:text-[13px]">
              <FixTip tip={tip} />
            </div>
          )}
        </div>
      </article>
    );
  }
  if (variant === "lead") {
    return (
      <article
        data-finding={code}
        data-severity={severity}
        className="flex flex-wrap items-start gap-4"
      >
        {icon}
        <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-2">
          <div className="flex items-start gap-1.5">
            <h3 className="text-2xl leading-tight font-semibold text-foreground">
              <span className="sr-only">{_(SEVERITY_LABELS[severity])}: </span>
              {title}
            </h3>
            {hint}
          </div>
          <p className="text-[15px] text-foreground-soft">{details}</p>
          {children}
          {finding !== null && (
            <FindingFixes
              finding={finding}
              className="mt-1 flex flex-col gap-1.5 rounded-[10px] bg-surface-raised px-4 py-3.5"
            />
          )}
        </div>
      </article>
    );
  }
  if (variant === "compact") {
    return (
      <article
        data-finding={code}
        data-severity={severity}
        className={cn(
          "flex items-center gap-3 rounded-[10px] border px-3 py-2.5",
          COMPACT_COLORS[severity],
        )}
      >
        {icon}
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-foreground">
              <span className="sr-only">{_(SEVERITY_LABELS[severity])}: </span>
              {title}
            </h3>
            {hint}
          </div>
          <p className="text-xs text-muted">{summary}</p>
        </div>
      </article>
    );
  }
  return (
    <article
      data-finding={code}
      data-severity={severity}
      className="flex gap-3 rounded-xl border border-border bg-surface p-4"
    >
      {icon}
      <div className="flex min-w-0 grow flex-col gap-1">
        <div className="flex items-start gap-1.5">
          <h4 className="flex items-start gap-2 text-[15px] font-semibold text-foreground">
            <SeverityDot severity={severity} className="mt-px size-[18px] text-[11px]" />
            <span className="sr-only">{_(SEVERITY_LABELS[severity])}: </span>
            <span>{title}</span>
          </h4>
          {hint}
        </div>
        <p className="text-sm text-muted">{details}</p>
        {children}
        {finding !== null && <FindingFixes finding={finding} />}
      </div>
    </article>
  );
};
